import React from "react";
import { Box, Button, Chip, LinearProgress, Typography } from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";

const BRAND_BLUE = "#00336C";
const BRAND_GREEN = "#648315";
const SUPPORTED_IMPORT_PLATFORMS = new Set([
  "eShow",
  "MapYourShow",
  "A2Z Events",
  "Sourcehere / Kallman",
  "GoExpo",
  "Automation India",
]);

export function isImportTerminal(status) {
  if (!status?.hasImportJob) return true;
  if (typeof status.terminal === "boolean") return status.terminal;
  return ["completed", "failed"].includes(status.status);
}

export function getAutoImportMessage(status) {
  if (!status?.hasImportJob) return "";
  const stage = status.currentStage;
  const platform = status.detectedPlatform || "";
  const discovered = status.exhibitorsDiscovered || 0;
  const imported = status.exhibitorsImported || 0;

  if (stage === "discovering_directory" || stage === "requested") {
    return "Searching for the official exhibitor directory...";
  }
  if (stage === "directory_not_found") {
    return "Automatic discovery could not find an exhibitor directory. You can upload a list manually.";
  }
  if ((stage === "directory_found" && platform && !SUPPORTED_IMPORT_PLATFORMS.has(platform)) || stage === "unsupported_platform") {
    return "An exhibitor directory was found, but automatic importing is not yet supported for this platform.";
  }
  if (stage === "directory_found" || stage === "extracting_exhibitors" || stage === "saving_exhibitors") {
    return `${platform || "Exhibitor"} directory found. Importing exhibitors...`;
  }
  if (stage === "enriching_exhibitors") {
    return `${imported} exhibitors added to the workspace. Enriching records...`;
  }
  if (stage === "status_unavailable") {
    return status.safeErrorMessage || "Automatic import is still being checked...";
  }
  if (stage === "completed_with_warnings") {
    return `${discovered} exhibitors discovered. ${imported} added to the workspace. Some records need review.`;
  }
  if (stage === "completed") {
    return `${discovered} exhibitors discovered. ${imported} added to the workspace.`;
  }
  if (stage === "failed" || status.status === "failed") {
    return status.safeErrorMessage || "Automatic import failed. You can upload a list manually.";
  }
  return "Searching for the official exhibitor directory...";
}

export function formatImportStatusLabel(value) {
  return String(value || "")
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

export default function AutoImportStatusPanel({ status, onManualUpload }) {
  if (!status?.hasImportJob) return null;
  const progress = Math.max(0, Math.min(100, Number(status.progressPercent || 0)));
  const message = getAutoImportMessage(status);
  const statusLabel = formatImportStatusLabel(status.currentStage || status.status);

  return (
    <Box
      data-testid="auto-import-status-panel"
      sx={{
        border: "1px solid #dce4ef",
        borderRadius: 2,
        backgroundColor: "#ffffff",
        p: 2,
        mb: 2,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}>
        <Box sx={{ display: "flex", gap: 1.25, minWidth: 0 }}>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: 2,
              display: "grid",
              placeItems: "center",
              backgroundColor: "rgba(0,51,108,0.08)",
              color: BRAND_BLUE,
              flexShrink: 0,
            }}
          >
            <TravelExploreIcon sx={{ fontSize: 19 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: "0.95rem", fontWeight: 800, color: BRAND_BLUE }}>
              Automatic Exhibitor Import
            </Typography>
            <Typography sx={{ fontSize: "0.84rem", color: "#4d5f75", mt: 0.35 }}>
              {message}
            </Typography>
          </Box>
        </Box>
        <Chip
          label={statusLabel}
          size="small"
          sx={{ height: 24, fontWeight: 800, color: BRAND_BLUE, backgroundColor: "rgba(0,51,108,0.08)" }}
        />
      </Box>

      <Box sx={{ mt: 1.75 }}>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 7,
            borderRadius: 999,
            backgroundColor: "#edf1f6",
            "& .MuiLinearProgress-bar": { backgroundColor: BRAND_GREEN },
          }}
        />
        <Typography sx={{ fontSize: "0.75rem", color: "#7a8fa8", mt: 0.75, fontWeight: 700 }}>
          {progress}% complete
        </Typography>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, minmax(0, 1fr))" },
          gap: 1,
          mt: 1.75,
        }}
      >
        {[
          ["Discovered", status.exhibitorsDiscovered || 0],
          ["Imported", status.exhibitorsImported || 0],
          ["Duplicates", status.duplicatesSkipped || 0],
          ["Failed", status.recordsFailed || 0],
        ].map(([label, value]) => (
          <Box key={label} sx={{ border: "1px solid #edf1f6", borderRadius: 1.5, p: 1 }}>
            <Typography sx={{ fontSize: "0.7rem", fontWeight: 800, color: BRAND_GREEN }}>{label}</Typography>
            <Typography sx={{ fontSize: "1rem", fontWeight: 800, color: BRAND_BLUE }}>{value}</Typography>
          </Box>
        ))}
      </Box>

      {status.safeErrorMessage && (
        <Box sx={{ mt: 1.5, display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Typography sx={{ fontSize: "0.78rem", color: "#B42318" }}>{status.safeErrorMessage}</Typography>
        </Box>
      )}

      <Box sx={{ mt: 1.75 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<UploadFileIcon />}
          onClick={onManualUpload}
          sx={{ textTransform: "none", fontWeight: 800, color: BRAND_BLUE, borderColor: BRAND_BLUE }}
        >
          Upload Exhibitor List
        </Button>
      </Box>
    </Box>
  );
}
